import { createClient } from '@/shared/api';
import NEW_REPORT_DATA_QUERY from '@/entities/report/api/queries/newReportData';
import NEW_REPORT_DATA_STUDY_QUERY from '@/entities/report/api/queries/newReportDataStudy';
import type { Patient, Insurance, Diagnosis } from '@/shared/api';

export type NewReportDataResult = {
  isSuccess: boolean;
  message: string;
  reportData: {
    // TODO(ts): queried as an opaque scalar (no sub-selection in the GraphQL document)
    holterProfile: any;
    study: {
      id: string;
      isArchived: boolean;
      friendlyId: string;
      start: string;
      stop: string;
      studyType: string;
      status: string;
      timezone: string;
      device: { id: string; deviceId: string };
      info: {
        patient: Patient;
        insurance: Insurance;
        diagnosis: Diagnosis;
      };
      facility: {
        id: string;
        name: string;
        contact: { fax: string; faxes: string[]; country: string };
        faxReportAutomatically: boolean;
        faxReportManually: boolean;
        facilityNote: string;
        canSendReportEmail: boolean;
        timezone: string;
      };
      processingAiStatus: string;
      aiReport: { status: string };
      isNonPhi: boolean;
    };
    report: {
      id: string;
      reportId: string;
      type: string;
      start: string;
      stop: string;
      status: string;
      technicianComments: string;
      isNotificationReport: boolean;
      isArtifactReport: boolean;
      inbox: { priority: string; date: string; lastEmailSentAt: string };
      isRenderingBackground: boolean;
    };
  };
};

export type NewReportDataStudyResult = {
  id: string;
  reports: { type: string; status: string }[];
};

/**
 * Loads the data backing a new report, either for one study or for a report request. Picks the study or newReportData query based on isStudyReport, returning the study's reports in the first case and the newReportData payload in the second, throwing its message when it is unsuccessful.
 *
 * @param input - Query input used when loading report data rather than a study.
 * @param isStudyReport - Whether to load a study's reports instead; defaults to false.
 * @param id - Study identifier used when isStudyReport is set.
 * @returns A promise resolving to the study's reports or to the new report data.
 */
const fetchNewReportData = async ({
  input,
  isStudyReport = false,
  id,
}: {
  input?: any;
  isStudyReport?: boolean;
  id?: string;
}) => {
  const client = await createClient();
  const result = await client.query<
    { study: NewReportDataStudyResult } | { newReportData: NewReportDataResult }
  >({
    query: isStudyReport ? NEW_REPORT_DATA_STUDY_QUERY : NEW_REPORT_DATA_QUERY,
    variables: isStudyReport ? { id } : { input },
  });
  const { data } = result;
  if (isStudyReport) {
    const { study } = data as { study: NewReportDataStudyResult };
    return study.reports;
  }
  const { newReportData } = data as { newReportData: NewReportDataResult };

  // *: Toast error instead
  if (newReportData?.isSuccess) {
    return newReportData;
  }
  throw new Error(newReportData.message);
};

export default fetchNewReportData;
